import { Controller } from "@hotwired/stimulus"

/**
 * 3D Cube Page Transition between the main dashboard and embedded Runway app.
 *
 * The main content and Runway iframe are positioned as two faces of a 3D cube.
 * Clicking "Runway" rotates the cube -90deg on Y to reveal the iframe face,
 * creating a dramatic context-switch effect.
 *
 * Inspired by: https://codepen.io/Hiteh/pen/OxjPGZ
 */
export default class extends Controller {
  static targets = ["cubeWrapper", "cube", "mainContent", "iframeContainer", "iframe", "loadingOverlay", "inloopTab", "studioTab", "mobileInloopTab", "mobileStudioTab", "workspaceTab", "modeStrip", "modeStripDivider", "mobileHeader", "appIcon", "appName", "openInNewTabLink"]
  static values = {
    url: String,
    settingsUrl: { type: String, default: "/settings#facilitator" },
    configured: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    loaded: { type: Boolean, default: false }
  }

  connect() {
    this._originalTitle = document.title
    this._onIframeLoad = this.onIframeLoad.bind(this)
    this._onIframeError = this.onIframeError.bind(this)
    this._onResize = this.updateCubeDimensions.bind(this)
    this._onPopState = this.handlePopState.bind(this)
    this.iframeTarget.addEventListener("load", this._onIframeLoad)
    this.iframeTarget.addEventListener("error", this._onIframeError)
    window.addEventListener("resize", this._onResize)
    window.addEventListener("popstate", this._onPopState)
    this.updateCubeDimensions()

    // If page loaded at /studio, auto-show
    if (window.location.pathname === "/studio") {
      this.show()
    }
  }

  disconnect() {
    this.iframeTarget.removeEventListener("load", this._onIframeLoad)
    this.iframeTarget.removeEventListener("error", this._onIframeError)
    window.removeEventListener("resize", this._onResize)
    window.removeEventListener("popstate", this._onPopState)
  }

  /**
   * Calculate cube depth from container width and position the right face.
   * Called on connect and window resize to stay responsive.
   */
  updateCubeDimensions() {
    if (!this.hasCubeWrapperTarget) return
    const width = this.cubeWrapperTarget.offsetWidth
    const half = width / 2

    // Set the cube's rotation origin at the center of the 3D cube
    this.cubeTarget.style.transformOrigin = `50% 50% -${half}px`

    // Position the right face: translate out + rotate 90deg to form the cube's right side
    this.iframeContainerTarget.style.transform =
      `translateX(${half}px) translateZ(-${half}px) rotateY(90deg)`
  }

  show(event) {
    if (event) event.preventDefault()

    // If no URL configured, redirect to facilitator settings
    if (!this.configuredValue) {
      window.Turbo ? Turbo.visit(this.settingsUrlValue) : (window.location.href = this.settingsUrlValue)
      return
    }

    this.activateApp({
      key: "studio",
      name: "Studio",
      icon: "/logo-studio.svg",
      iframeUrl: this.embeddedStudioUrl(),
      externalUrl: this.urlValue,
      historyState: { studio: true },
      historyPath: "/studio"
    })
  }

  showShortcut(event) {
    event.preventDefault()
    const tab = event.currentTarget
    const id = tab.dataset.workspaceShortcutId

    this.activateApp({
      key: `shortcut-${id}`,
      name: tab.dataset.workspaceShortcutName,
      icon: tab.dataset.workspaceShortcutIcon,
      iframeUrl: tab.dataset.workspaceShortcutUrl,
      externalUrl: tab.dataset.workspaceShortcutUrl,
      historyState: { workspaceShortcut: id },
      historyPath: this._previousPath || window.location.pathname + window.location.search
    })
  }

  activateApp({ key, name, icon, iframeUrl, externalUrl, historyState, historyPath }) {
    const wasActive = this.activeValue
    if (!wasActive) {
      this._previousPath = window.location.pathname === "/studio" ? "/facilitator/inloops" : window.location.pathname + window.location.search
    }

    this.activeValue = true
    this._activeAppKey = key
    this._activeShortcutId = key.startsWith("shortcut-") ? key.replace("shortcut-", "") : null
    this._activeAppName = name
    this._activeAppIcon = icon

    this.updateAppHeader(name, icon, externalUrl)
    this.loadIframe(iframeUrl, key)
    this.updateNavActiveState(true)
    this.updateModeStrip(true)
    this.updateWorkspaceTabStates(this._activeShortcutId)
    this.updateCubeDimensions()

    if (wasActive) {
      history.replaceState(historyState, "", historyPath)
    } else {
      history.pushState(historyState, "", historyPath)
    }

    requestAnimationFrame(() => {
      this.cubeTarget.style.transform = "rotateY(-90deg)"
    })
  }

  loadIframe(url, key) {
    if (this.iframeTarget.dataset.activeApp === key) return

    this.loadedValue = false
    this.iframeTarget.dataset.activeApp = key
    this.iframeTarget.src = url
    this.showLoading()
  }

  updateAppHeader(name, icon, externalUrl) {
    if (this.hasAppNameTarget) this.appNameTarget.textContent = name
    if (this.hasAppIconTarget) this.appIconTarget.src = icon || "/logo-studio.svg"
    if (this.hasOpenInNewTabLinkTarget) this.openInNewTabLinkTarget.href = externalUrl
  }

  embeddedStudioUrl() {
    const url = new URL(this.urlValue, window.location.origin)
    if (url.pathname === "/" || url.pathname === "") url.pathname = "/dashboard"
    url.searchParams.set("embedded", "1")
    return url.toString()
  }

  hide(event) {
    if (event) event.preventDefault()
    if (!this.activeValue) return
    this.activeValue = false
    this._activeAppKey = null
    this._activeShortcutId = null
    this.updateNavActiveState(false)
    this.updateModeStrip(false)
    this.updateWorkspaceTabStates(null)

    // Restore previous URL
    history.pushState({}, "", this._previousPath || "/facilitator/inloops")

    // Rotate cube back to front face (main content)
    this.cubeTarget.style.transform = "rotateY(0deg)"
  }

  handlePopState(event) {
    if (event.state?.studio && !this.activeValue) {
      this.show()
    } else if (!event.state?.studio && this.activeValue) {
      // Browser back from /studio — flip back without pushing state again
      this.activeValue = false
      this._activeAppKey = null
      this._activeShortcutId = null
      this.updateNavActiveState(false)
      this.updateModeStrip(false)
      this.updateWorkspaceTabStates(null)
      this.cubeTarget.style.transform = "rotateY(0deg)"
    }
  }

  onIframeLoad() {
    if (!this.loadedValue && this.iframeTarget.src !== "about:blank") {
      // Try to detect error pages (same-origin only)
      try {
        const doc = this.iframeTarget.contentDocument
        if (doc && (doc.title.match(/not found|404|error/i) || doc.querySelector("[data-error-page]"))) {
          this.onIframeError()
          return
        }
      } catch (_) {
        // Cross-origin — can't inspect, that's fine
      }
      this.loadedValue = true
      this.hideLoading()
    }
  }

  onIframeError() {
    if (this._activeShortcutId && this.hasOpenInNewTabLinkTarget) {
      window.open(this.openInNewTabLinkTarget.href, "_blank", "noopener,noreferrer")
      this.hide()
    } else {
      this.redirectToSettings()
    }
  }

  faviconError(event) {
    event.currentTarget.removeAttribute("data-action")
    event.currentTarget.src = "/logo-studio.svg"
  }

  redirectToSettings() {
    this.activeValue = false
    this.cubeTarget.style.transform = "rotateY(0deg)"
    this.hideLoading()
    window.Turbo ? Turbo.visit(this.settingsUrlValue) : (window.location.href = this.settingsUrlValue)
  }

  showLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.style.transition = "opacity 400ms ease"
      this.loadingOverlayTarget.style.opacity = "0"
      setTimeout(() => {
        this.loadingOverlayTarget.classList.add("hidden")
        this.loadingOverlayTarget.style.removeProperty("transition")
        this.loadingOverlayTarget.style.removeProperty("opacity")
      }, 400)
    }
  }

  /**
   * Update right mode strip, mobile tabs, favicon, and title to reflect active view.
   */
  updateModeStrip(externalAppActive) {
    const studioActive = externalAppActive && !this._activeShortcutId

    // Desktop mode strip — entire strip changes theme
    if (this.hasModeStripTarget) {
      if (externalAppActive) {
        this.modeStripTarget.classList.remove("bg-white", "border-[#f2f3f5]")
        this.modeStripTarget.classList.add("bg-gray-900", "border-gray-800")
      } else {
        this.modeStripTarget.classList.remove("bg-gray-900", "border-gray-800")
        this.modeStripTarget.classList.add("bg-white", "border-[#f2f3f5]")
      }
    }

    // Desktop Inloop state
    if (this.hasInloopTabTarget) {
      if (externalAppActive) {
        this.inloopTabTarget.classList.remove("bg-brand-50", "border-2", "border-brand-200")
        this.inloopTabTarget.classList.add("hover:bg-gray-800")
        this.inloopTabTarget.querySelector("span").classList.remove("text-brand-700")
        this.inloopTabTarget.querySelector("span").classList.add("text-gray-500")
      } else {
        this.inloopTabTarget.classList.remove("hover:bg-gray-800")
        this.inloopTabTarget.classList.add("bg-brand-50", "border-2", "border-brand-200")
        this.inloopTabTarget.querySelector("span").classList.remove("text-gray-500")
        this.inloopTabTarget.querySelector("span").classList.add("text-brand-700")
      }
    }

    // Desktop Studio state
    if (this.hasStudioTabTarget) {
      if (studioActive) {
        this.studioTabTarget.classList.remove("hover:bg-gray-50", "hover:bg-gray-800")
        this.studioTabTarget.classList.add("bg-gray-800", "border-2", "border-gray-600")
        this.studioTabTarget.querySelector("span").classList.remove("text-gray-500")
        this.studioTabTarget.querySelector("span").classList.add("text-gray-100")
      } else {
        this.studioTabTarget.classList.remove("bg-gray-800", "border-2", "border-gray-600")
        this.studioTabTarget.classList.remove("hover:bg-gray-50", "hover:bg-gray-800")
        this.studioTabTarget.classList.add(externalAppActive ? "hover:bg-gray-800" : "hover:bg-gray-50")
        this.studioTabTarget.querySelector("span").classList.remove("text-gray-100")
        this.studioTabTarget.querySelector("span").classList.add("text-gray-500")
      }
    }

    // Divider
    if (this.hasModeStripDividerTarget) {
      if (externalAppActive) {
        this.modeStripDividerTarget.classList.remove("bg-gray-200")
        this.modeStripDividerTarget.classList.add("bg-gray-700")
      } else {
        this.modeStripDividerTarget.classList.remove("bg-gray-700")
        this.modeStripDividerTarget.classList.add("bg-gray-200")
      }
    }

    // Mobile header
    if (this.hasMobileHeaderTarget) {
      if (externalAppActive) {
        this.mobileHeaderTarget.classList.remove("bg-white", "border-[#F2F3F5]")
        this.mobileHeaderTarget.classList.add("bg-gray-900", "border-gray-800")
        this.mobileHeaderTarget.querySelectorAll(".text-\\[\\#030712\\]").forEach(el => {
          el.classList.remove("text-[#030712]")
          el.classList.add("text-gray-100")
        })
      } else {
        this.mobileHeaderTarget.classList.remove("bg-gray-900", "border-gray-800")
        this.mobileHeaderTarget.classList.add("bg-white", "border-[#F2F3F5]")
        this.mobileHeaderTarget.querySelectorAll(".text-gray-100").forEach(el => {
          el.classList.remove("text-gray-100")
          el.classList.add("text-[#030712]")
        })
      }
    }

    // Mobile Inloop state and switcher theme
    if (this.hasMobileInloopTabTarget) {
      if (externalAppActive) {
        this.mobileInloopTabTarget.classList.remove("bg-white", "shadow-sm", "text-brand-700")
        this.mobileInloopTabTarget.classList.add("text-gray-400")
        this.mobileInloopTabTarget.parentElement.classList.remove("bg-gray-100")
        this.mobileInloopTabTarget.parentElement.classList.add("bg-gray-800")
      } else {
        this.mobileInloopTabTarget.classList.remove("text-gray-400")
        this.mobileInloopTabTarget.classList.add("bg-white", "shadow-sm", "text-brand-700")
        this.mobileInloopTabTarget.parentElement.classList.remove("bg-gray-800")
        this.mobileInloopTabTarget.parentElement.classList.add("bg-gray-100")
      }
    }

    // Mobile Studio state
    if (this.hasMobileStudioTabTarget) {
      if (studioActive) {
        this.mobileStudioTabTarget.classList.remove("text-gray-500")
        this.mobileStudioTabTarget.classList.add("bg-gray-800", "shadow-sm", "text-gray-100")
      } else {
        this.mobileStudioTabTarget.classList.remove("bg-gray-800", "shadow-sm", "text-gray-100")
        this.mobileStudioTabTarget.classList.add("text-gray-500")
      }
    }

    // Favicon + title
    const favicon = document.querySelector('link[rel="icon"][type="image/png"]')
    const faviconSvg = document.querySelector('link[rel="icon"][type="image/svg+xml"]')
    if (externalAppActive) {
      const name = this._activeAppName || "Studio"
      const icon = this._activeAppIcon || "/logo-studio.svg"
      document.title = name
      if (favicon) favicon.href = icon
      if (faviconSvg) faviconSvg.href = icon
    } else {
      document.title = this._originalTitle || "App Inloop Studio"
      if (favicon) favicon.href = "/logo.png"
      if (faviconSvg) faviconSvg.href = "/logo.svg"
    }
  }

  updateWorkspaceTabStates(activeId) {
    this.workspaceTabTargets.forEach((tab) => {
      const active = tab.dataset.workspaceShortcutId === activeId
      const label = tab.querySelector("span")

      if (active) {
        tab.classList.remove("hover:bg-gray-50", "hover:bg-white/70", "text-gray-500")
        tab.classList.add("bg-gray-800", "border-2", "border-gray-600", "text-gray-100")
        if (label) {
          label.classList.remove("text-gray-500")
          label.classList.add("text-gray-100")
        }
      } else {
        tab.classList.remove("bg-gray-800", "border-2", "border-gray-600", "text-gray-100")
        tab.classList.add("text-gray-500")
        tab.classList.remove("hover:bg-gray-50", "hover:bg-gray-800", "hover:bg-white/70")
        tab.classList.add(tab.classList.contains("flex-col") ? (this.activeValue ? "hover:bg-gray-800" : "hover:bg-gray-50") : "hover:bg-white/70")
        if (label) {
          label.classList.remove("text-gray-100")
          label.classList.add("text-gray-500")
        }
      }
    })
  }

  /**
   * Update sidebar nav items to reflect which view is active.
   */
  updateNavActiveState(runwayActive) {
    document.querySelectorAll("[data-runway-nav]").forEach((item) => {
      if (runwayActive) {
        item.classList.add("bg-[#fafafb]", "text-[#111827]")
      } else {
        item.classList.remove("bg-[#fafafb]", "text-[#111827]")
      }
    })

    // Remove active highlight from other nav items when Runway is shown
    if (runwayActive) {
      document.querySelectorAll("[data-sidebar-target~='navItem']:not([data-runway-nav])").forEach((item) => {
        item.classList.remove("bg-[#fafafb]", "text-[#111827]")
      })
    }
  }
}
